import { Box, Stack, Typography } from "@mui/material";
import { BusinessAddressFormFields } from "Components/Onboarding/BusinessEntity/Steps/BusinessInformationStep/BusinessAddressFormFields";
import type { BusinessInformationStepSchema } from "Components/Onboarding/BusinessEntity/Steps/BusinessInformationStep/BusinessInformationStepContent";
import {
  IS_VAT_NUMBER_FROM_REGISTRY,
  isVatQuestionOpen,
} from "Schemas/vatNumberSchema";
import { ValuesBox } from "Components/Onboarding/shared/ValuesBox";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { BusinessDropdown } from "Components/Shared/SelectorsForm/BusinessDropdown";
import { useRegionList } from "Hooks/Options/useRegionList";
import type { Region, VatStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { removeNonDigits } from "Utils/StringUtils";
import type { ReactNode } from "react";
import type { Control } from "react-hook-form";

const COMPONENT_RESOURCES =
  Resources.Onboarding.BusinessOnboarding.Steps.BusinessInformation;

type BusinessInformationFieldsProps = {
  control: Control<BusinessInformationStepSchema>;
  region: Region | undefined;
  isRegionLocked: boolean;
  isCompanyNumberDisabled: boolean;
  vatRegistryStatus: VatStatus;
  // The IČ DPH input (see VatNumberField); rendered only where the business register does not publish
  // the number, so the caller does not need to know that rule.
  vatNumberField: ReactNode;
  onRegionChange: (region: Region) => void;
};

export const BusinessInformationFields = ({
  control,
  region,
  isRegionLocked,
  isCompanyNumberDisabled,
  vatRegistryStatus,
  vatNumberField,
  onRegionChange,
}: BusinessInformationFieldsProps) => {
  const { t } = useResource();
  const { codeListRegions } = useRegionList();
  const isVatNumberFromRegistry = region
    ? IS_VAT_NUMBER_FROM_REGISTRY[region]
    : true;

  return (
    <>
      <ValuesBox
        title={t(COMPONENT_RESOURCES.FormGroups.BasicInformation)}
        hideDivider
      >
        <Dropdown
          codeList={codeListRegions}
          control={control}
          name="region"
          label={t(COMPONENT_RESOURCES.RegionLabel)}
          disabled={isRegionLocked}
          onChange={(value) => onRegionChange(value as Region)}
        />
        <FormInput
          control={control}
          name="companyNumber"
          label={t(Resources.Common.CompanyNumber)}
          valueTransformer={removeNonDigits}
          disabled={isCompanyNumberDisabled}
        />
        <FormInput
          disabled
          control={control}
          name="companyName"
          label={t(Resources.Common.CompanyName)}
        />
        <Stack gap={1}>
          <FormInput
            disabled
            control={control}
            name="taxNumber"
            label={t(Resources.Common.TaxNumber)}
          />
          {/* CZ: the IČ DPH is the DIČ, so the register's word belongs right under it. Nothing while the
              register has not answered — there is no field to act on here. */}
          {isVatNumberFromRegistry && !isVatQuestionOpen(vatRegistryStatus) && (
            <Typography variant="body2" color="text.secondary">
              {t(Resources.Common.VatRegistryStatus, {
                status: t(Resources.Common.VatStatus[vatRegistryStatus]),
              })}
            </Typography>
          )}
        </Stack>
        {/* Hidden in a market whose register publishes the number: there it equals the DIČ shown right
            above, so a second read-only copy of it only reads as a form bug. The value still travels in
            the payload, and a correction (a payer the register does not list yet) goes through admin. */}
        {!isVatNumberFromRegistry && vatNumberField}
        <BusinessDropdown
          control={control}
          name="type"
          label={t(Resources.Common.PartyTypeLabel)}
          isDisabled
        />
      </ValuesBox>
      <BusinessAddressFormFields control={control} />
      <Box>
        <ValuesBox
          title={t(COMPONENT_RESOURCES.FormGroups.Contact)}
          hideDivider
        >
          <PhoneInput
            control={control}
            name="phone"
            label={t(Resources.Common.Phone)}
          />
          <FormInput
            control={control}
            name="email"
            label={t(Resources.Common.Email)}
          />
        </ValuesBox>
      </Box>
    </>
  );
};
